import { useState } from "react";
import { FileText, History, Loader2, Search } from "lucide-react";
import { toast } from "react-toastify";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Exportar a conversa em PDF.
//
// Levar um atendimento para fora do sistema — um processo, uma auditoria, o
// cliente que pede o que foi combinado — só dava para fazer tirando print de
// tela, um por um. Três caminhos, porque as três perguntas são diferentes:
// "quero ESTE atendimento", "quero a conversa inteira deste cliente" e "quero
// aquele chamado lá de trás, sei o número".
const ExportarConversaModal = ({ aberto, aoFechar, ticketId, contato }) => {
	const [numero, setNumero] = useState("");
	const [baixando, setBaixando] = useState("");

	const baixar = async (alvo, historico, chave) => {
		setBaixando(chave);

		try {
			const { data, headers } = await api.get(`/messages/${alvo}/export`, {
				params: { historico: historico ? "true" : undefined },
				responseType: "blob",
			});

			const nome =
				(headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] ||
				`atendimento-${alvo}.pdf`;

			const url = window.URL.createObjectURL(new Blob([data]));
			const link = document.createElement("a");
			link.href = url;
			link.download = nome;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			toast.success("O download do PDF começou.");
			aoFechar();
		} catch (err) {
			// O erro vem como blob quando a resposta seria JSON: sem isto, o
			// aviso na tela sairia "[object Blob]".
			if (err?.response?.data instanceof Blob) {
				toast.error(
					Number(numero) && chave === "numero"
						? `Atendimento #${numero} não encontrado ou sem acesso.`
						: "Não foi possível gerar o PDF.",
				);
			} else {
				toastError(err);
			}
		}

		setBaixando("");
	};

	return (
		<Dialog open={aberto} onOpenChange={(estado) => !estado && aoFechar()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Exportar conversa para PDF</DialogTitle>
					<DialogDescription>
						{contato ? `Conversa com ${contato}.` : "Escolha o que exportar."}
					</DialogDescription>
				</DialogHeader>

				<form
					className="space-y-2 rounded-lg border border-primary/40 bg-primary/5 p-3"
					onSubmit={async (e) => {
						e.preventDefault();
						const entrada = numero.split("-")[0].replace(/\D/g, "");
						if (!entrada) return;
						try {
							// Resolve o numero (novo OU antigo, que vive nas faixas
							// importadas) para o atendimento certo e exporta a conversa dele.
							const { data } = await api.get(
								`/tickets/por-numero/${entrada}`,
							);
							baixar(data.ticketId, false, "numero");
						} catch {
							toast.error(`Atendimento #${entrada} não encontrado.`);
						}
					}}
				>
					<p className="flex items-center gap-2 text-sm font-medium">
						<Search className="h-4 w-4 text-primary" />
						Exportar por número do atendimento
					</p>
					<p className="text-xs text-muted-foreground">
						Digite o número do atendimento que deseja exportar.
					</p>
					<Input
						value={numero}
						onChange={(e) => setNumero(e.target.value.replace(/[^\d-]/g, ""))}
						placeholder="Ex: 12345"
						inputMode="numeric"
						aria-label="Número do atendimento"
					/>
					<Button
						type="submit"
						className="w-full gap-2"
						disabled={!numero || Boolean(baixando)}
					>
						{baixando === "numero" ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<FileText className="h-4 w-4" />
						)}
						Exportar atendimento #{numero || "?"}
					</Button>
				</form>

				<div className="flex items-center gap-2">
					<span className="h-px flex-1 bg-border" />
					<span className="text-[11px] text-muted-foreground">ou</span>
					<span className="h-px flex-1 bg-border" />
				</div>

				<button
					type="button"
					onClick={() => baixar(ticketId, false, "atual")}
					disabled={Boolean(baixando)}
					className="flex w-full items-center gap-3 rounded-lg border p-3 text-left hover:bg-muted/50 disabled:opacity-60"
				>
					{baixando === "atual" ? (
						<Loader2 className="h-5 w-5 shrink-0 animate-spin text-primary" />
					) : (
						<FileText className="h-5 w-5 shrink-0 text-primary" />
					)}
					<span>
						<span className="block text-sm font-medium">
							Atendimento atual (#{ticketId})
						</span>
						<span className="block text-xs text-muted-foreground">
							Exporta somente as mensagens deste atendimento.
						</span>
					</span>
				</button>

				<div className="flex items-center gap-2">
					<span className="h-px flex-1 bg-border" />
					<span className="text-[11px] text-muted-foreground">ou</span>
					<span className="h-px flex-1 bg-border" />
				</div>

				<button
					type="button"
					onClick={() => baixar(ticketId, true, "historico")}
					disabled={Boolean(baixando)}
					className="flex w-full items-center gap-3 rounded-lg border p-3 text-left hover:bg-muted/50 disabled:opacity-60"
				>
					{baixando === "historico" ? (
						<Loader2 className="h-5 w-5 shrink-0 animate-spin text-primary" />
					) : (
						<History className="h-5 w-5 shrink-0 text-primary" />
					)}
					<span>
						<span className="block text-sm font-medium">
							Histórico completo do contato
						</span>
						<span className="block text-xs text-muted-foreground">
							Todos os atendimentos deste contato, separados por número.
						</span>
					</span>
				</button>
			</DialogContent>
		</Dialog>
	);
};

export default ExportarConversaModal;
